// src/api/redditApi.ts
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
    clearAuthData,
    isAuthenticated, // Checks if refresh token or valid access token exists
    getValidAccessToken // Handles refresh logic
} from '../utils/redditAuth';
import { RedditComment, RedditMoreComments } from '../types/reddit';
import { generateCacheKey, getFromCache, setInCache, clearCache } from './cache';

// Base URL for authenticated Reddit API requests
const OAUTH_BASE_URL = 'https://oauth.reddit.com';

// Create an Axios instance
const apiClient: AxiosInstance = axios.create({
    baseURL: OAUTH_BASE_URL,
});

// --- Interceptors ---
apiClient.interceptors.request.use(
    async (config) => {
        // Check if potentially authenticated (has refresh or valid access token)
        if (!isAuthenticated()) {
            console.warn('Not authenticated. Aborting API request.');
            clearAuthData(); // Ensure cleanup
            clearCache();    // Clear cache on auth failure
            if (window.location.pathname !== '/settings' && window.location.pathname !== '/callback') {
                window.location.href = '/settings'; // Redirect to settings to log in
            }
            return Promise.reject(new axios.Cancel('Not authenticated.'));
        }

        // Get a valid access token (refreshes if necessary)
        const accessToken = await getValidAccessToken();

        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
            return config;
        } else {
            // Refresh failed or no tokens available
            console.error('Failed to obtain valid access token. Aborting request.');
            clearAuthData(); // Ensure cleanup
            clearCache();    // Clear cache on auth failure
            if (window.location.pathname !== '/settings' && window.location.pathname !== '/callback') {
                window.location.href = '/settings'; // Redirect to settings
            }
            return Promise.reject(new axios.Cancel('Failed to obtain valid access token.'));
        }
    },
    (error) => {
        // Handle request setup errors
        return Promise.reject(error);
    }
);

apiClient.interceptors.response.use(
    (response) => response, // Pass through successful responses
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle 401/403 errors (likely expired token) - Try refreshing token once
        if (error.response && (error.response.status === 401 || error.response.status === 403) && !originalRequest._retry) {
            console.warn(`API request failed with ${error.response.status}. Attempting token refresh (fallback).`);
            originalRequest._retry = true; // Mark as retry attempt

            try {
                const newAccessToken = await getValidAccessToken(); // Attempt refresh
                if (newAccessToken) {
                    console.log("Token refreshed successfully (fallback). Retrying original request.");
                    // Update the authorization header for the original request
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    // Retry the original request with the new token
                    return apiClient(originalRequest);
                } else {
                    // Refresh failed
                    console.error("Fallback token refresh failed. Logging out.");
                    clearAuthData(); // Ensure cleanup
                    clearCache();    // Clear cache on auth failure
                    if (window.location.pathname !== '/settings' && window.location.pathname !== '/callback') {
                        window.location.href = '/settings'; // Redirect to settings
                    }
                    return Promise.reject(new Error("Token refresh failed, user logged out."));
                }
            } catch (refreshError) {
                console.error("Error during fallback token refresh:", refreshError);
                 // clearAuthData() should be called within getValidAccessToken/refreshToken on failure
                 if (window.location.pathname !== '/settings' && window.location.pathname !== '/callback') {
                     window.location.href = '/settings'; // Redirect on refresh error
                 }
                return Promise.reject(refreshError);
            }
        }

        // Handle cancellations specifically (e.g., from auth logic)
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            // Don't automatically reject cancellations if they originated from auth logic
            // Let the originating logic handle redirects/rejections
            return Promise.reject(error);
        }

        // For other errors, log and reject
        console.error('API Error (Response Interceptor):', error);
        return Promise.reject(error);
    }
);


// --- Specific API Call Functions ---

export const getMe = async (): Promise<any> => {
    const path = '/api/v1/me';
    try {
        // Use direct apiClient call - ensures fresh data and handles auth correctly via interceptors
        const response = await apiClient.get<any>(path);
        return response.data;
    } catch (error: any) {
        // Interceptors handle cancellations and auth errors, just log others
        if (!axios.isCancel(error)) { console.error('Error fetching user info:', error); }
        throw error; // Re-throw to be handled by caller
    }
};

export const getSubredditPosts = async (
    subreddit: string,
    sort: 'hot' | 'new' | 'top' | 'rising' = 'hot',
    params: Record<string, any> = {}
): Promise<any> => {
    const queryParams = { limit: 25, ...params };
    const path = subreddit ? `/r/${subreddit}/${sort}` : `/${sort}`;
    const cacheKey = generateCacheKey(path, queryParams);
    const cachedData = getFromCache<any>(cacheKey);

    // Return cached data if valid and not null/undefined
    // NOTE: We still treat cached `null` as a miss here, which is correct.
    if (cachedData !== undefined && cachedData !== null) {
        console.log(`[Cache HIT] Returning posts for ${subreddit || 'home'} from cache. Key: ${cacheKey}`);
        return Promise.resolve(cachedData);
    }

    console.log(`[Cache MISS] Fetching posts for ${subreddit || 'home'}. Key: ${cacheKey}`);
    try {
        // Interceptors handle auth
        const response = await apiClient.get<any>(path, { params: queryParams });
        console.log(`[Cache SET] Caching posts response for ${subreddit || 'home'}. Key: ${cacheKey}`);
        setInCache(cacheKey, response.data); // Cache successful response
        return response.data;
    } catch (error: any) {
        // Interceptors handle cancellations and auth errors
        if (!axios.isCancel(error)) {
            console.error(`Error fetching posts for ${subreddit ? 'r/' + subreddit : 'home feed'} (${sort}):`, error);
        }
        throw error; // Re-throw, don't cache errors here
    }
};

export const getSubredditAbout = async (subreddit: string): Promise<any> => {
    if (!subreddit) { return Promise.reject(new Error("Subreddit name cannot be empty for getSubredditAbout.")); }
    const cleanSubreddit = subreddit.replace(/^\/?r\//, '');
    const path = `/r/${cleanSubreddit}/about`;
    const cacheKey = generateCacheKey(path);
    const cachedData = getFromCache<any>(cacheKey);

    // Check cache first
    // If null was cached previously (e.g., from an old version or a 404 we *used* to cache),
    // treat it as a cache miss to force a refetch.
    if (cachedData !== undefined) {
        if (cachedData === null) {
            console.log(`[Cache WARN] Found cached null for getSubredditAbout key: ${cacheKey}. Treating as miss and refetching.`);
            // Fall through to refetch
        } else {
            // Valid cached data exists
            console.log(`[Cache HIT] Returning /about for ${cleanSubreddit} from cache. Key: ${cacheKey}`);
            return Promise.resolve(cachedData);
        }
    }

    // Fetch fresh data if cache miss or invalid/null cache data
    console.log(`[Cache MISS] Fetching /about for ${cleanSubreddit}. Key: ${cacheKey}`);
    try {
        // Interceptors handle auth
        const response = await apiClient.get<any>(path);
        if (response.data?.kind !== 't5') {
            console.error(`[API Error] Unexpected response kind for /about ${cleanSubreddit}:`, response.data?.kind);
            throw new Error(`Unexpected response kind received for /about: ${response.data?.kind}`);
        }
        console.log(`[Cache SET] Caching /about response for ${cleanSubreddit}. Key: ${cacheKey}`);
        setInCache(cacheKey, response.data); // Cache successful response
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            console.warn(`[API 404] Subreddit r/${cleanSubreddit} not found. Returning null without caching null. Key: ${cacheKey}`);
            // *** THE FIX: Return null but DO NOT cache it ***
            // setInCache(cacheKey, null); // Ensure this line is removed or commented out
            return null; // Return null for 404 for this attempt
        } else if (!axios.isCancel(error)) {
            console.error(`Error fetching about info for r/${cleanSubreddit}:`, error);
        }
        throw error; // Re-throw other errors
    }
};


export const getMySubscribedSubreddits = async (
    params: Record<string, any> = {}
): Promise<any> => {
    const queryParams = { limit: 100, ...params };
    const path = '/subreddits/mine/subscriber';
    const cacheKey = generateCacheKey(path, queryParams);
    const cachedData = getFromCache<any>(cacheKey);

    // Return cached data if valid and not null/undefined
    if (cachedData !== undefined && cachedData !== null) {
         console.log(`[Cache HIT] Returning subscribed subreddits from cache. Key: ${cacheKey}`);
        return Promise.resolve(cachedData);
    }

    console.log(`[Cache MISS] Fetching subscribed subreddits. Key: ${cacheKey}`);
    try {
        // Interceptors handle auth
        const response = await apiClient.get<any>(path, { params: queryParams });
        console.log(`[Cache SET] Caching subscribed subreddits response. Key: ${cacheKey}`);
        setInCache(cacheKey, response.data);
        return response.data;
    } catch (error: any) {
        // Interceptors handle cancellations and auth errors
        if (!axios.isCancel(error)) {
            console.error('Error fetching subscribed subreddits:', error);
        }
        throw error;
    }
};

// --- SPLIT Post and Comments Fetching ---

export const getPostDetails = async (
    postId: string,
    subreddit?: string,
    params: Record<string, any> = {}
): Promise<any> => { // Returns the Post Listing or null
    const cleanPostId = postId.startsWith('t3_') ? postId.substring(3) : postId;
    const path = subreddit ? `/r/${subreddit}/comments/${cleanPostId}` : `/comments/${cleanPostId}`;
    // Use specific params for fetching post details, typically shallow
    const queryParams = { limit: 1, depth: 1, ...params };
    const cacheKey = generateCacheKey(path, queryParams);
    const cachedData = getFromCache<[any, any] | null>(cacheKey);

    // Check cache first
    if (cachedData !== undefined) {
        // If null was cached previously (e.g., from an old version or a 404 we *used* to cache),
        // treat it as a cache miss to force a refetch.
        if (cachedData === null) {
            console.log(`[Cache WARN] Found cached null for getPostDetails key: ${cacheKey}. Treating as miss and refetching.`);
            // Fall through to refetch
        }
        // Validate structure of non-null cached data
        else if (Array.isArray(cachedData) && cachedData.length >= 1 && cachedData[0]?.kind === 'Listing' && cachedData[0].data?.children?.length > 0) {
             console.log(`[Cache HIT] Returning post details for ${postId} from cache. Key: ${cacheKey}`);
            return cachedData[0]; // Return the post listing part
        } else {
            // If cached data exists but is invalid
            console.warn(`[Cache WARN] Invalid cached data structure for getPostDetails key: ${cacheKey}. Refetching.`);
            // Fall through to refetch
        }
    }

    // Fetch fresh data if cache miss or invalid/null cache data
    console.log(`[Cache MISS] Fetching post details for ${postId}. Key: ${cacheKey}`);
    try {
        // Interceptors handle auth
        const response = await apiClient.get<[any, any]>(path, { params: queryParams });
        const responseData = response.data;

        // Validate API response structure before caching/returning
        if (
            !Array.isArray(responseData) || responseData.length < 1 ||
            responseData[0]?.kind !== 'Listing' || // Check post listing kind
            !responseData[0]?.data?.children || // Check data.children exists
            responseData[0].data.children.length === 0 // Check children array is not empty
        ) {
             console.error(`[API Error] Invalid or empty post data received from API for ${postId}:`, responseData);
             // Don't cache invalid data. Throw error so PostPage can handle it.
             throw new Error('Invalid or empty post data received from API.');
        }

        // If validation passes, cache the full tuple and return the post part
        console.log(`[Cache SET] Caching post details response for ${postId}. Key: ${cacheKey}`);
        setInCache(cacheKey, responseData); // Only cache successful, valid responses
        return responseData[0];
    } catch (error: any) {
         if (axios.isAxiosError(error) && error.response?.status === 404) {
             console.warn(`[API 404] Post ${cleanPostId} not found. Returning null without caching null. Key: ${cacheKey}`);
             // *** THE FIX: Return null but DO NOT cache it ***
             // setInCache(cacheKey, null); // Ensure this line is removed or commented out
             return null; // Return null to indicate 404 for this attempt
         } else if (!axios.isCancel(error)) {
             // Log other errors (including the validation error thrown above)
             console.error(`[API Error] Error fetching or validating post details ${postId}:`, error);
         }
         // Re-throw the error to be handled by the caller (PostPage)
         throw error;
    }
};

export const getComments = async (
    postId: string,
    subreddit?: string,
    params: Record<string, any> = {}
): Promise<any> => { // Returns the Comment Listing
    const cleanPostId = postId.startsWith('t3_') ? postId.substring(3) : postId;
    const path = subreddit ? `/r/${subreddit}/comments/${cleanPostId}` : `/comments/${cleanPostId}`;
    // Use specific params for fetching comments, typically deeper
    const queryParams = { limit: 25, depth: 5, ...params };
    const cacheKey = generateCacheKey(path, queryParams);
    const cachedData = getFromCache<[any, any] | null>(cacheKey);

    const emptyCommentsListing = { kind: 'Listing', data: { children: [], after: null, before: null, dist: 0, modhash: '' } };

    // Check cache first
    if (cachedData !== undefined) {
        // If null was cached previously, treat as miss
        if (cachedData === null) {
            console.log(`[Cache WARN] Found cached null for getComments key: ${cacheKey}. Treating as miss and refetching.`);
            // Fall through
        }
        // Validate structure of non-null cached data
        else if (Array.isArray(cachedData) && cachedData.length >= 2 && cachedData[1]?.kind === 'Listing') {
             console.log(`[Cache HIT] Returning comments for ${postId} from cache. Key: ${cacheKey}`);
            return cachedData[1]; // Return the comment listing part
        } else {
             console.warn(`[Cache WARN] Invalid cached data structure for getComments key: ${cacheKey}. Refetching.`);
             // Fall through
        }
    }

    // Fetch fresh data if cache miss or invalid/null cache data
    console.log(`[Cache MISS] Fetching comments for ${postId}. Key: ${cacheKey}`);
    try {
        // Interceptors handle auth
        const response = await apiClient.get<[any, any]>(path, { params: queryParams });
        const responseData = response.data;

        // Validate comment structure before caching/returning
        if (!Array.isArray(responseData) || responseData.length < 2 || responseData[1]?.kind !== 'Listing') {
             // Handle cases where comments might be empty or structure is unexpected *without* throwing an error necessarily
             if (responseData && Array.isArray(responseData) && responseData.length >= 1 && responseData[0]?.kind === 'Listing') {
                 console.log(`[API OK] No comments found or invalid comment structure for post ${postId}. Returning empty comments.`);
                 // Cache the full response even if comments are missing/invalid, to cache the post data at least
                 setInCache(cacheKey, responseData);
                 return emptyCommentsListing;
             }
             // If the structure is completely wrong, throw error
             console.error(`[API Error] Invalid response structure for comments ${postId}:`, responseData);
             throw new Error('Invalid response structure for comments');
        }
        // If validation passes, cache the full tuple and return the comments part
        console.log(`[Cache SET] Caching comments response for ${postId}. Key: ${cacheKey}`);
        setInCache(cacheKey, responseData);
        return responseData[1];
    } catch (error: any) {
         if (axios.isAxiosError(error) && error.response?.status === 404) {
             console.warn(`[API 404] Post ${cleanPostId} not found when fetching comments. Returning empty comments without caching null. Key: ${cacheKey}`);
             // *** THE FIX: Return empty but DO NOT cache null ***
             // setInCache(cacheKey, null); // Ensure this line is removed or commented out
             return emptyCommentsListing; // Return empty on 404, don't cache failure
         } else if (!axios.isCancel(error)) {
             console.error(`[API Error] Error fetching comments for ${postId}:`, error);
         }
         // Re-throw other errors. Consider returning emptyCommentsListing if error handling should be more graceful.
         throw error;
    }
};


export const getMoreComments = async (
    postId: string,
    children: string[],
    sort?: string
): Promise<(RedditComment | RedditMoreComments)[]> => {
    try {
        const linkId = postId.startsWith('t3_') ? postId : `t3_${postId}`;
        const params: Record<string, any> = { api_type: 'json', children: children.join(','), link_id: linkId, };
        if (sort) { params.sort = sort; }
        // Use direct apiClient call, interceptors handle auth/refresh
        // Don't cache 'more' requests as they are dynamic
        const response = await apiClient.post('/api/morechildren', new URLSearchParams(params).toString(), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });

        if (response.data?.json?.errors?.length > 0) {
            console.error('Errors fetching more comments:', response.data.json.errors);
            throw new Error(`Failed to load some comments: ${response.data.json.errors.join(', ')}`);
        }
        const things = response.data?.json?.data?.things;
        if (!Array.isArray(things)) {
            console.warn('No comments returned from morechildren.');
            return [];
        }
        // Filter for valid comment ('t1') or more ('more') kinds
        return things.filter(thing => thing.kind === 't1' || thing.kind === 'more') as (RedditComment | RedditMoreComments)[];
    } catch (error: any) {
        // Interceptors handle cancellations and auth errors
        if (!axios.isCancel(error)) {
            console.error(`Error fetching more comments for post ${postId}:`, error);
        }
        throw error; // Re-throw
    }
};

// --- Search Functions (Using caching) ---

// Helper for search caching - checks cache first
async function makeSearchRequest<T>(path: string, params: Record<string, any>): Promise<T> {
    const cacheKey = generateCacheKey(path, params);
    const cachedData = getFromCache<T>(cacheKey);
    // Return cached data if valid and not null/undefined
    if (cachedData !== undefined && cachedData !== null) {
        console.log(`[Cache HIT] Returning search results from cache. Key: ${cacheKey}`);
        return Promise.resolve(cachedData);
    }

    console.log(`[Cache MISS] Fetching search results. Key: ${cacheKey}`);
    try {
        // Interceptors handle auth
        const response = await apiClient.get<T>(path, { params });
        console.log(`[Cache SET] Caching search results response. Key: ${cacheKey}`);
        setInCache(cacheKey, response.data); // Cache successful search results
        return response.data;
    } catch (error) {
        // Don't cache errors for searches
        console.error(`[API Error] Error fetching search results for ${path}:`, error);
        throw error;
    }
}


export const searchSubredditPosts = async (
    subreddit: string,
    query: string,
    params: Record<string, any> = {}
): Promise<any> => {
    const cleanSubreddit = subreddit.replace(/^\/?r\//, '');
    if (!cleanSubreddit) { throw new Error("Subreddit name cannot be empty for subreddit search."); }
    const queryParams = { q: query, restrict_sr: 'true', limit: 25, ...params };
    const path = `/r/${cleanSubreddit}/search`;
    try {
        return await makeSearchRequest<any>(path, queryParams);
    } catch (error: any) {
        // Error already logged in helper, just re-throw
        throw error;
    }
};

export const searchSubreddits = async (
    query: string,
    params: Record<string, any> = {}
): Promise<any> => {
    const queryParams = { q: query, limit: 25, ...params };
    const path = '/subreddits/search';
    try {
        return await makeSearchRequest<any>(path, queryParams);
    } catch (error: any) {
        // Error already logged in helper, just re-throw
        throw error;
    }
};

export const searchGlobalPosts = async (
    query: string,
    params: Record<string, any> = {}
): Promise<any> => {
    const queryParams = { q: query, limit: 25, ...params };
    const path = '/search';
    try {
        return await makeSearchRequest<any>(path, queryParams);
    } catch (error: any) {
        // Error already logged in helper, just re-throw
        throw error;
    }
};